import React, { PureComponent, Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { withRouter } from 'react-router-dom'
import CheckBox from 'components/common/CheckBox'
import { saveKeystoreToNative, getNativeKeystore } from 'utils/storage'
import Confirm from 'components/common/Confirm'
import Icon from '../../common/Icon'
import Button from '../../common/Button'
import UploadImg from '../../../static/images/upload.svg'
import errorIcon from '../../../static/images/error-icon.svg'
import InputPassword from '../../common/InputPassword'
import { Consumer as BNCClientConsumer } from '../../../containers/context/BNCClientProvider'
import { withI18n } from '../../../i18n/util'
import { setFingerPrint } from '../../../utils/fingerPrint'
import isElectron from '../../../utils/isElectron'

const Wrapper = styled.form`
  width: 100%;
  color: #48515d;
  position: relative;
  padding-bottom: 40px;
`

const Title = styled.div`
  font-size: 14px;
  line-height: 18px;
  font-weight: normal;
  padding: 10px 0;
  color: #48515d;
  padding-top: 50px;
  cursor: pointer;
  span:hover {
    color: #f0b90b;
  }
  .show:hover {
    color: inherit;
  }
`

const UploadKeystore = styled.div`
  height: 40px;
  line-height: 40px;
  text-align: center;
  background: #fff;
  color: #f0b90b;
  font-size: 16px;
  border: 1px solid #f0b90b;
  cursor: pointer;
  border-radius: 3px;
  position: relative;
  display: flex;
  justify-content: center;
  margin-top: 10px;
  img {
    width: 24px;
    margin-right: 5px;
  }
  span {
    font-weight: bold;
    transition: transform 0.25s ease;
  }
  &:hover span {
    transform: scale(0.95);
  }
  i {
    margin-right: 10px;
  }
  input {
    width: 100%;
    height: 100%;
    opacity: 0;
    display: block;
    top: 0;
    left: 0;
    position: absolute;
    cursor: pointer;
  }
`

const TextArea = styled.textarea`
  height: 70px;
  padding: 10px;
  width: 95%;
  border: 1px solid #f0f0f0;
  outline: none;
  resize: none;
`

const Row = styled.div`
  margin: 40px 0;
  font-size: 14px;
`

export const FootRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;

  @media (min-width: 320px) and (max-width: 623px) {
    flex-direction: column;
    margin: 20px 0;
    padding-bottom: 40px;
    .create-link {
      order: 1;
      margin-top: 10px;
    }
    .unlock {
      order: 0;
    }
  }
`

export const GoCreate = styled.a`
  font-size: 14px;
  color: #f0b90b;
  cursor: pointer;
`

const SuccessMark = styled.div`
  position: absolute;
  right: 20px;
  top: 11px;
  svg {
    position: absolute;
    top: 3px;
    left: 2px;
    fill: none;
    stroke: #00c087;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 16px;
    stroke-dashoffset: ${props => (props.show ? '0' : '16px')};
    transition: all 0.3s ease;
    transition-delay: 0.1s;
    transform: translate3d(0, 0, 0);
  }
`

const ErrorMsg = styled.div`
  color: #f23051;
  position: absolute;
  left: 0;
  right: 0;
  font-size: 14px;
  display: flex;
  height: 20px;
  img {
    width: 15px;
    margin-right: 5px;
  }
`

class UnlockFromKeystore extends PureComponent {
  static propTypes = {
    unlock: PropTypes.func,
    history: PropTypes.object,
    formatI18nText: PropTypes.func,
    deviceInfo: PropTypes.object,
  }

  static defaultProps = {
    unlock: () => {},
    history: {},
    formatI18nText: () => {},
    deviceInfo: {},
  }

  constructor(props) {
    super(props)
    this.state = {
      password: '',
      keystore: '',
      errMsg: '',
      loading: false,
      showTextArea: false,
      isSaveToNative: false,
      showConfirmSaveKeystore: false,
    }
  }

  componentWillUnmount() {
    clearTimeout(this.timoutHandle)
  }

  _unlock = bncClient => ev => {
    ev.preventDefault()
    const { formatI18nText } = this.props
    const { keystore, isSaveToNative } = this.state

    if (!keystore) {
      this.setState({ errMsg: formatI18nText('unlock.thisIsNotAValidWalletFile'), loading: false })
    }

    if (isSaveToNative) {
      getNativeKeystore().then(file => {
        if (file) {
          this.setState({ showConfirmSaveKeystore: true })
        } else {
          this._continue(bncClient)
        }
      })
    } else {
      this._continue(bncClient)
    }
  }

  _continue = bncClient => {
    const { unlock, deviceInfo } = this.props
    const { password, keystore, isSaveToNative } = this.state
    this.setState({ loading: true })

    if (isSaveToNative) {
      ;(async () => {
        await saveKeystoreToNative(keystore)
      })()
    }

    this.timoutHandle = setTimeout(() => {
      try {
        const { privateKey, address } = bncClient.recoverAccountFromKeystore(keystore, password)
        bncClient.useDefaultSigningDelegate()
        unlock(privateKey, address, keystore, password)
        setFingerPrint(deviceInfo, 'unlock_keystore', address, '')
      } catch (e) {
        this.setState({ errMsg: e.message, loading: false })
      }
    }, 100)
  }

  _fileChange = e => {
    const { formatI18nText } = this.props
    const reader = new FileReader()
    reader.readAsText(e.currentTarget.files[0], 'UTF-8')
    reader.onload = evt => {
      try {
        JSON.parse(evt.target.result)
        this.setState({ keystore: evt.target.result, errMsg: '' })
      } catch (err) {
        this.setState({ errMsg: formatI18nText('unlock.thisIsNotAValidWalletFile'), keystore: '' })
      }
    }
  }

  _textAreaChange = e => {
    this.setState({ keystore: e.target.value })
  }

  _showTextArea = () => {
    this.setState({ showTextArea: true })
  }

  _hideTextArea = () => {
    this.setState({ showTextArea: false })
  }

  _passwordChange = password => {
    this.setState({ password })
  }

  _gotoCreate = ev => {
    ev.preventDefault()
    const { history } = this.props
    history.push('/create')
  }

  _savetoNative = e => {
    document.activeElement.blur() // andorid hide keyboard
    this.setState({ isSaveToNative: e.target.checked })
  }

  _confirmSaveToNative = bncClient => () => {
    this.setState({ showConfirmSaveKeystore: false })
    this._continue(bncClient)
  }

  _hide = () => {
    this.setState({ showConfirmSaveKeystore: false })
  }

  render() {
    const {
      keystore,
      errMsg,
      loading,
      showTextArea,
      isSaveToNative,
      showConfirmSaveKeystore,
    } = this.state
    const { formatI18nText } = this.props

    return (
      <BNCClientConsumer>
        {bncClient => (
          <Wrapper onSubmit={this._unlock(bncClient)}>
            <Title onClick={this._hideTextArea}>
              <span className={!showTextArea ? 'show' : ''}>
                {formatI18nText('unlock.keystore.select')}
              </span>
            </Title>
            {!showTextArea && (
              <UploadKeystore key="1">
                <img src={UploadImg} alt={true} />
                <span>{formatI18nText('unlock.keystore.upload')}</span>
                <input type="file" title=" " onChange={this._fileChange} />
                <SuccessMark show={!!keystore}>
                  <svg width="12px" height="10px" viewBox="0 0 12 10">
                    <polyline points="1.5 6 4.5 9 10.5 1" />
                  </svg>
                </SuccessMark>
              </UploadKeystore>
            )}
            {showTextArea && (
              <TextArea
                key="2"
                onChange={this._textAreaChange}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                data-gramm={false}
              />
            )}
            <Row>
              <InputPassword
                type="password"
                withRules={false}
                title={formatI18nText('unlock.keystore.enterYourWalletPass')}
                onChange={this._passwordChange}
              />
              {isElectron() && (
                <CheckBox
                  labelTitle={formatI18nText('local.secure.storage')}
                  defaultChecked={isSaveToNative}
                  handleCheckChange={this._savetoNative}
                />
              )}
            </Row>
            {errMsg && (
              <ErrorMsg>
                <img src={errorIcon} alt="" />
                <span>{errMsg}</span>
              </ErrorMsg>
            )}
            <FootRow>
              <GoCreate className="goCreate create-link" onClick={this._gotoCreate}>
                <span>{formatI18nText('unlock.createNewWallet')}</span>
              </GoCreate>
              <Button
                type="submit"
                loading={loading}
                width="170px"
                id="Unlock_Keystore_File"
                className="unlock"
              >
                <Fragment>
                  <span
                    style={{
                      marginRight: '10px',
                    }}
                  >
                    {formatI18nText('unlock.unlockWalletNow')}
                  </span>
                  <Icon type="continue" size="20" color="inherit" />
                </Fragment>
              </Button>
            </FootRow>
            {showConfirmSaveKeystore && (
              <Confirm
                okText={formatI18nText('common.confirm')}
                cancelText={formatI18nText('common.cancel')}
                confirm={this._confirmSaveToNative(bncClient)}
                cancel={this._hide}
              >
                <p>You already have a keystore file, make sure you have backed up before replace</p>
              </Confirm>
            )}
          </Wrapper>
        )}
      </BNCClientConsumer>
    )
  }
}

export default withI18n(withRouter(UnlockFromKeystore))
